import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, BarChart, Bar, Legend, PieChart, Pie, Cell } from "recharts";
import { Plus, FileText, Users, AlertCircle, TrendingUp, Receipt, DollarSign, Clock, Calendar, Activity, Wrench, Zap, BarChart3, Briefcase } from "lucide-react";
import api from "../lib/apiClient";
import { fmtCAD, fmtDate } from "../lib/format";
import HcpDashboard from "./dashboard/HcpDashboard";

const PIE_COLORS = ["#0F4C81", "#0EA5E9", "#10B981", "#F59E0B", "#EF4444", "#8B5CF6", "#EC4899", "#64748B"];

const Kpi = ({ label, value, sub, accent, testId, icon: Icon }) => (
  <div data-testid={testId} className="card-hover bg-white border border-slate-200 rounded-md p-6">
    <div className="flex items-start justify-between">
      <div className="flex-1">
        <div className="text-[11px] font-medium text-slate-500 uppercase tracking-wider">{label}</div>
        <div className="mt-3 font-display text-3xl font-bold text-slate-900 tabular tracking-tight">{value}</div>
        {sub && <div className={`mt-1 text-xs ${accent || "text-slate-500"}`}>{sub}</div>}
      </div>
      {Icon && <div className="w-9 h-9 rounded-md bg-[#E0F2FE] flex items-center justify-center"><Icon size={18} className="text-[#0F4C81]" strokeWidth={1.5} /></div>}
    </div>
  </div>
);

function fmtDateTime(iso) { if (!iso) return "—"; try { return new Date(iso).toLocaleString("en-CA", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); } catch { return iso; } }

export default function Dashboard() {
  const [d, setD] = useState(null);
  const [ext, setExt] = useState(null);
  const [v3, setV3] = useState(null);
  const [customers, setCustomers] = useState({});
  const [loading, setLoading] = useState(true);
  const [chartsReady, setChartsReady] = useState(false);

  useEffect(() => {
    // Defer chart mount until after first paint so ResponsiveContainer
    // measures a settled DOM layout (eliminates recharts width(-1) warnings).
    let t;
    const raf = requestAnimationFrame(() => {
      const raf2 = requestAnimationFrame(() => {
        t = setTimeout(() => setChartsReady(true), 30);
      });
      return () => cancelAnimationFrame(raf2);
    });
    return () => { cancelAnimationFrame(raf); if (t) clearTimeout(t); };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const [{ data: a }, { data: b }, { data: c }, { data: cs }] = await Promise.all([
          api.get("/dashboard"), api.get("/dashboard/extra"), api.get("/dashboard/v3"), api.get("/customers"),
        ]);
        if (cancelled) return;
        setD(a); setExt(b); setV3(c); setCustomers(Object.fromEntries(cs.map((c) => [c.id, c])));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    // Refresh dashboard when the user returns to the tab or comes back from another page
    const onVis = () => { if (document.visibilityState === "visible") load(); };
    const onFocus = () => load();
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("focus", onFocus);
    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("focus", onFocus);
    };
  }, []);

  if (loading) return <div data-testid="dashboard-loading" className="text-sm text-slate-500">Loading dashboard...</div>;
  if (!d) return null;

  return (
    <div data-testid="dashboard-page" className="space-y-8">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">Dashboard</h1>
          <p className="text-sm text-slate-500 mt-1">Snapshot of your business performance.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Link to="/jobs/new" data-testid="quick-new-job" className="px-4 py-2 bg-[#0F4C81] hover:bg-[#0A365C] text-white text-sm rounded-md font-medium flex items-center gap-2 transition-colors">
            <Briefcase size={16} /> New Job
          </Link>
          <Link to="/estimates/new" data-testid="quick-new-estimate" className="px-4 py-2 bg-white border border-slate-300 text-slate-700 text-sm rounded-md font-medium hover:bg-slate-50 flex items-center gap-2 transition-colors">
            <FileText size={16} /> New Estimate
          </Link>
          <Link to="/customers/new" data-testid="quick-new-customer" className="px-4 py-2 bg-white border border-slate-300 text-slate-700 text-sm rounded-md font-medium hover:bg-slate-50 flex items-center gap-2 transition-colors">
            <Users size={16} /> New Customer
          </Link>
        </div>
      </div>

      {d.overdue_count > 0 && (
        <div data-testid="overdue-alert" className="border border-red-200 bg-red-50 rounded-md p-4 flex items-start gap-3">
          <AlertCircle className="text-red-600 mt-0.5" size={20} />
          <div>
            <div className="font-semibold text-red-900">{d.overdue_count} overdue invoice{d.overdue_count > 1 ? "s" : ""}</div>
            <div className="text-sm text-red-700 mt-0.5">Total overdue: <span className="font-semibold tabular">{fmtCAD(d.overdue_total)}</span> · <Link to="/invoices?status=Overdue" className="underline">Review</Link></div>
          </div>
        </div>
      )}

      {/* HCP-style at-a-glance grid */}
      <HcpDashboard />

      <div className="border-t border-slate-200 pt-6">
        <h2 className="font-display text-lg font-semibold text-slate-900 mb-1">Detailed analytics</h2>
        <p className="text-xs text-slate-500 mb-4">Long-term performance, A/R aging, and tax compliance.</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <Kpi testId="kpi-paid-mtd" label="Paid (this month)" value={fmtCAD(d.paid_invoices_mtd)} sub="Month to date" icon={DollarSign} />
        <Kpi testId="kpi-outstanding" label="Outstanding" value={fmtCAD(d.outstanding_total)} sub={`${d.total_invoices} total invoices`} icon={Receipt} />
        <Kpi testId="kpi-expenses-mtd" label="Expenses (MTD)" value={fmtCAD(ext?.expenses_mtd || 0)} sub={`${fmtCAD(ext?.expenses_ytd || 0)} YTD`} icon={Wrench} />
        <Kpi testId="kpi-hours-week" label="Hours this week" value={(ext?.hours_this_week || 0).toFixed(1) + "h"} sub={`${ext?.active_jobs?.length || 0} active job${(ext?.active_jobs?.length || 0) === 1 ? "" : "s"}`} icon={Clock} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div data-testid="revenue-chart" className="lg:col-span-2 bg-white border border-slate-200 rounded-md p-6">
          <div className="flex justify-between items-center mb-4">
            <div>
              <h3 className="font-display text-lg font-semibold text-slate-900">Revenue trend</h3>
              <p className="text-xs text-slate-500">Last 12 months · payments received</p>
            </div>
            <div className="text-xs text-slate-500">Avg invoice: <span className="font-semibold text-slate-900 tabular">{fmtCAD(d.average_invoice)}</span></div>
          </div>
          <div className="h-72 w-full" style={{ minWidth: 0 }}>
            {chartsReady && (
            <ResponsiveContainer width="99%" height="100%" debounce={50}>
              <LineChart data={d.revenue_trend} margin={{ top: 10, right: 10, bottom: 0, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#64748b" }} axisLine={{ stroke: "#e2e8f0" }} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false} tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} />
                <Tooltip formatter={(v) => fmtCAD(v)} contentStyle={{ borderRadius: 6, border: "1px solid #e2e8f0", fontSize: 12 }} />
                <Line type="monotone" dataKey="revenue" stroke="#0F4C81" strokeWidth={2.5} dot={{ fill: "#0F4C81", r: 3 }} activeDot={{ r: 5 }} />
              </LineChart>
            </ResponsiveContainer>
            )}
          </div>
        </div>

        <div data-testid="aging-buckets" className="bg-white border border-slate-200 rounded-md p-6">
          <h3 className="font-display text-lg font-semibold text-slate-900">A/R Aging</h3>
          <p className="text-xs text-slate-500 mb-4">Outstanding by age bucket</p>
          <div className="space-y-3">
            {Object.entries(d.aging_buckets).map(([k, v]) => {
              const maxv = Math.max(...Object.values(d.aging_buckets), 1);
              const pct = (v / maxv) * 100;
              const color = k === "90+" ? "bg-red-500" : k === "61-90" ? "bg-orange-500" : k === "31-60" ? "bg-amber-400" : "bg-emerald-500";
              return (
                <div key={k} data-testid={`aging-${k}`}>
                  <div className="flex justify-between text-xs mb-1">
                    <span className="text-slate-600">{k} days</span>
                    <span className="font-semibold text-slate-900 tabular">{fmtCAD(v)}</span>
                  </div>
                  <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                    <div className={`h-full ${color} rounded-full transition-all`} style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div data-testid="hst-chart" className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-md p-6">
          <h3 className="font-display text-lg font-semibold flex items-center gap-2"><BarChart3 size={18} className="text-[#0F4C81]" /> HST Collected vs Paid (6mo)</h3>
          <p className="text-xs text-slate-500 mb-3">Output tax vs Input Tax Credits</p>
          <div className="h-64 w-full" style={{ minWidth: 0 }}>
            {chartsReady && (
            <ResponsiveContainer width="99%" height="100%" debounce={50}>
              <BarChart data={v3?.hst_trend || []}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false} tickFormatter={(v) => `$${v}`} />
                <Tooltip formatter={(v) => fmtCAD(v)} contentStyle={{ borderRadius: 6, border: "1px solid #e2e8f0", fontSize: 12 }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="collected" fill="#0F4C81" name="Collected" radius={[4, 4, 0, 0]} />
                <Bar dataKey="paid" fill="#10B981" name="Paid (ITCs)" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
            )}
          </div>
        </div>

        <div data-testid="service-type-chart" className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-md p-6">
          <h3 className="font-display text-lg font-semibold">Revenue by Service Type (YTD)</h3>
          <p className="text-xs text-slate-500 mb-3">Top revenue-generating job types</p>
          <div className="h-64 w-full" style={{ minWidth: 0 }}>
            {Object.keys(v3?.revenue_by_type || {}).length === 0 ? (
              <div className="h-full flex items-center justify-center text-sm text-slate-500">No service revenue yet.</div>
            ) : chartsReady ? (
              <ResponsiveContainer width="99%" height="100%" debounce={50}>
                <PieChart>
                  <Pie data={Object.entries(v3.revenue_by_type).map(([name, value]) => ({ name, value }))}
                       dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80}
                       label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}>
                    {Object.keys(v3.revenue_by_type).map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                  </Pie>
                  <Tooltip formatter={(v) => fmtCAD(v)} />
                </PieChart>
              </ResponsiveContainer>
            ) : null}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div data-testid="upcoming-jobs-widget" className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-md p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display text-lg font-semibold flex items-center gap-2"><Calendar size={18} className="text-[#0F4C81]" /> Upcoming Jobs (7d)</h3>
            <Link to="/schedule" className="text-xs text-[#0F4C81] hover:underline">View calendar →</Link>
          </div>
          <div className="space-y-2">
            {(ext?.upcoming_jobs || []).length === 0 && <div className="text-sm text-slate-500 py-6 text-center">No upcoming jobs.</div>}
            {(ext?.upcoming_jobs || []).slice(0, 5).map((j) => (
              <Link key={j.id} to={`/jobs/${j.id}`} className="flex justify-between items-center bg-slate-50 border border-slate-200 rounded p-3 hover:border-slate-300">
                <div>
                  <div className="font-medium text-sm">{customers[j.customer_id]?.name || "Unknown"} · {j.job_type}</div>
                  <div className="text-xs text-slate-500 flex items-center gap-1"><Clock size={11} />{fmtDateTime(j.scheduled_start)}</div>
                </div>
                <span className={`text-xs px-2 py-0.5 rounded ${j.priority === "high" ? "bg-red-100 text-red-800" : j.priority === "low" ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-700"} capitalize`}>{j.priority}</span>
              </Link>
            ))}
          </div>
        </div>

        <div data-testid="top-profitable-widget" className="bg-white border border-slate-200 rounded-md p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display text-lg font-semibold flex items-center gap-2"><TrendingUp size={18} className="text-[#0F4C81]" /> Top Profitable Jobs (MTD)</h3>
            <Link to="/jobs" className="text-xs text-[#0F4C81] hover:underline">All jobs →</Link>
          </div>
          <div className="space-y-2">
            {(ext?.top_profitable_jobs || []).length === 0 && <div className="text-sm text-slate-500 py-6 text-center">No completed jobs yet this month.</div>}
            {(ext?.top_profitable_jobs || []).map((p) => (
              <Link key={p.job_id} to={`/jobs/${p.job_id}`} className="flex justify-between items-center bg-slate-50 border border-slate-200 rounded p-3 hover:border-slate-300">
                <div>
                  <div className="font-medium text-sm font-mono">{p.number}</div>
                  <div className="text-xs text-slate-500">{customers[p.customer_id]?.name || "—"}</div>
                </div>
                <div className="text-right">
                  <div className={`font-bold text-sm tabular ${p.profit >= 0 ? "text-emerald-700" : "text-red-600"}`}>{fmtCAD(p.profit)}</div>
                  <div className="text-xs text-slate-500 tabular">{fmtCAD(p.revenue)} rev</div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
